if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.progress = this.querySelector('.product-gallery-progress');
        if (this.progress && this.dataset.mobileProductSizing === 'product') this.initMobileProgress();
        if (!this.elements.thumbnails) return;

        const desktopSlideChanged = debounce((event) => {
          if (this.mql.matches || this.dataset.mobileProductSizing !== 'product') this.onSlideChanged(event);
        }, 500);
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          if (!this.mql.matches && this.dataset.mobileProductSizing === 'product') this.onSlideChanged(event);
          else desktopSlideChanged(event);
        });
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => {
          this.elements.viewer.addEventListener(type, () => this.cancelPendingMediaSelection(), { passive: true });
        });
        const interruptThumbnailScroll = () => {
          this.stopThumbnailScroll();
          this.thumbnailInteractionUntil = performance.now() + 250;
        };
        this.elements.thumbnails.addEventListener('pointerdown', interruptThumbnailScroll, { passive: true });
        this.elements.thumbnails.addEventListener('wheel', interruptThumbnailScroll, { passive: true });
        this.elements.thumbnails.addEventListener('touchstart', () => {
          this.thumbnailTouchActive = true;
          interruptThumbnailScroll();
        }, { passive: true });
        ['touchend', 'touchcancel'].forEach((type) => {
          this.elements.thumbnails.addEventListener(type, () => {
            this.thumbnailTouchActive = false;
            this.thumbnailInteractionUntil = performance.now() + 250;
          }, { passive: true });
        });
        this.elements.thumbnails.slider?.addEventListener('scroll', () => {
          if (!this.thumbnailScrollFrame && Math.abs(this.elements.thumbnails.slider.scrollLeft -
              (this.thumbnailWrittenPosition || 0)) > 1) this.thumbnailManuallyScrolled = true;
          if (!this.thumbnailScrollFrame) this.thumbnailInteractionUntil = performance.now() + 250;
        }, { passive: true });
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          button.addEventListener('mousedown', (event) => this.focusThumbnail(event));
          button.addEventListener('click', (event) => this.selectThumbnail(mediaToSwitch.dataset.target, event));
          button.addEventListener('keydown', () => button.removeAttribute('data-pointer-focus'));
          button.addEventListener('blur', () => button.removeAttribute('data-pointer-focus'));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        const mobile = !this.mql.matches && this.dataset.mobileProductSizing === 'product';
        if (mobile && this.pendingMediaSelection) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (mobile && thumbnail?.querySelector?.('button')?.getAttribute('aria-current') === 'true') return;
        const userScrolling = this.thumbnailTouchActive || performance.now() < (this.thumbnailInteractionUntil || 0);
        this.setActiveThumbnail(thumbnail, mobile ? 'smooth' : 'instant', !mobile || !userScrolling);
      }

      cancelPendingMediaSelection() {
        this.pendingMediaSelection = null;
      }

      focusThumbnail(event) {
        if (this.mql.matches || this.dataset.mobileProductSizing !== 'product') return;
        event.preventDefault();
        event.currentTarget.toggleAttribute('data-pointer-focus',
          event.type === 'mousedown' || event.detail > 0 || Boolean(event.pointerType));
        event.currentTarget.focus({ preventScroll: true });
      }

      selectThumbnail(mediaId, event) {
        const mobile = !this.mql.matches && this.dataset.mobileProductSizing === 'product';
        const advanceTo = this.fourthThumbnailAdvanceTarget(mediaId);
        if (mobile) this.focusThumbnail(event);
        this.setActiveMedia(mediaId, false, false, mobile ? 'smooth' : 'instant');
        if (advanceTo !== null) {
          this.scrollThumbnailTo(advanceTo, 'smooth');
        }
      }

      fourthThumbnailAdvanceTarget(mediaId) {
        if (this.mql.matches || this.dataset.mobileProductSizing !== 'product' ||
            this.dataset.mobileThumbnailCount !== '4' || this.thumbnailManuallyScrolled) return null;
        const slider = this.elements.thumbnails.slider;
        if (!slider) return null;
        const style = getComputedStyle(slider);
        const inset = parseFloat(style.paddingLeft) || 0;
        const endInset = parseFloat(style.paddingRight) || 0;
        const items = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          .filter((item) => item.clientWidth > 0);
        const visible = items.filter((item) => item.offsetLeft >= slider.scrollLeft + inset - 1 &&
          item.offsetLeft + item.clientWidth <= slider.scrollLeft + slider.clientWidth - endInset + 1);
        if (visible.length !== 4 || visible[3].dataset.target !== mediaId || visible[3] === items.at(-1)) return null;
        return visible[3].offsetLeft - inset;
      }

      stopThumbnailScroll() {
        if (this.thumbnailScrollFrame) cancelAnimationFrame(this.thumbnailScrollFrame);
        this.thumbnailScrollFrame = null;
      }

      scrollThumbnailPage(direction) {
        const count = Number(this.dataset.mobileThumbnailCount);
        if (this.mql.matches || this.dataset.mobileProductSizing !== 'product' ||
            ![3, 4, 5].includes(count)) return false;
        const slider = this.elements.thumbnails.slider;
        const items = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          .filter((item) => item.clientWidth > 0);
        if (!slider || !items.length) return false;
        const inset = parseFloat(getComputedStyle(slider).paddingLeft) || 0;
        const position = Math.max(0, Math.min(slider.scrollWidth - slider.clientWidth, slider.scrollLeft));
        let nearest = 0;
        items.forEach((item, index) => {
          if (Math.abs(item.offsetLeft - inset - position) < Math.abs(items[nearest].offsetLeft - inset - position)) {
            nearest = index;
          }
        });
        const target = Math.max(0, Math.min(Math.max(0, items.length - count), nearest + direction * count));
        this.scrollThumbnailTo(items[target].offsetLeft - inset, 'smooth');
        return true;
      }

      scrollThumbnailTo(left, behavior) {
        this.stopThumbnailScroll();
        const slider = this.elements.thumbnails.slider;
        left = Math.max(0, Math.min(slider.scrollWidth - slider.clientWidth, left));
        if (behavior !== 'smooth' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.thumbnailWrittenPosition = left;
          slider.scrollTo({ left, behavior: 'instant' });
          return;
        }
        const from = slider.scrollLeft;
        let started;
        const step = (time) => {
          if (this.mql.matches) { this.stopThumbnailScroll(); return; }
          started ??= time;
          const progress = Math.min(1, (time - started) / 300);
          this.thumbnailWrittenPosition = from + (left - from) * Math.sin(progress * Math.PI / 2);
          slider.scrollTo({ left: this.thumbnailWrittenPosition, behavior: 'instant' });
          this.thumbnailScrollFrame = progress < 1 ? requestAnimationFrame(step) : null;
        };
        this.thumbnailScrollFrame = requestAnimationFrame(step);
      }

      setActiveMedia(mediaId, prepend, instant = false, thumbnailBehavior = 'instant') {
        instant = instant === true;
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        const mobileProduct = !this.mql.matches && this.dataset.mobileProductSizing === 'product';
        this.pendingMediaSelection = mobileProduct ? mediaId : null;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        const scrollToMedia = () => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: instant ? 'instant' : 'auto' });
          }
          if (!mobileProduct && !instant && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        };
        if (instant || mobileProduct) scrollToMedia();
        else window.setTimeout(scrollToMedia);
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail, thumbnailBehavior);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      initMobileProgress() {
        this.progressInput = this.progress.querySelector('input');
        this.mediaList = this.elements.viewer.querySelector('[id^="Slider-Gallery-"]');
        this.queueProgressUpdate = () => {
          if (this.progressFrame) return;
          this.progressFrame = requestAnimationFrame(() => {
            this.progressFrame = null;
            this.updateMobileProgress();
          });
        };
        this.progressInput.addEventListener('input', () => {
          const items = this.mobileProgressItems();
          const item = items[Number(this.progressInput.value) - 1];
          if (item) this.setActiveMedia(item.dataset.mediaId, false, true);
          this.queueProgressUpdate();
        });
        this.mediaList.addEventListener('scroll', this.queueProgressUpdate, { passive: true });
        this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails-"]')
          ?.addEventListener('scroll', this.queueProgressUpdate, { passive: true });
        this.progressResize = new ResizeObserver(this.queueProgressUpdate);
        this.progressMutation = new MutationObserver(this.queueProgressUpdate);
      }

      connectedCallback() {
        if (!this.progressInput) return;
        this.progressResize.observe(this);
        this.progressResize.observe(this.progressInput);
        this.progressMutation.observe(this.mediaList, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
        this.mql.addEventListener('change', this.queueProgressUpdate);
        this.queueProgressUpdate();
      }

      disconnectedCallback() {
        this.cancelPendingMediaSelection();
        this.stopThumbnailScroll();
        this.progressResize?.disconnect();
        this.progressMutation?.disconnect();
        if (this.queueProgressUpdate) this.mql.removeEventListener('change', this.queueProgressUpdate);
        if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
        this.progressFrame = null;
      }

      mobileProgressItems() {
        return Array.from(this.mediaList.querySelectorAll('.product__media-item')).filter((item) =>
          !item.hidden && !item.classList.contains('variant-media-hidden') && item.clientWidth > 0
        );
      }

      updateMobileProgress() {
        if (this.mql.matches) return;
        this.updateMobileThumbnailButtons();
        const items = this.mobileProgressItems();
        this.progress.hidden = items.length < 2;
        this.progressInput.disabled = items.length < 2;
        this.progressInput.max = String(Math.max(1, items.length));
        if (!items.length) return;
        const start = items[0].offsetLeft;
        let current = 0;
        let distance = Infinity;
        items.forEach((item, index) => {
          const delta = Math.abs(item.offsetLeft - start - this.mediaList.scrollLeft);
          if (delta < distance) { current = index; distance = delta; }
        });
        this.progressInput.value = String(current + 1);
        this.progressInput.setAttribute('aria-valuetext', this.progressInput.dataset.valueText
          .replace('[current]', current + 1).replace('[total]', items.length));
        const segment = Math.max(8, this.progressInput.clientWidth / items.length);
        this.progressInput.style.setProperty('--mobile-product-bar-segment', `${segment}px`);
      }

      updateMobileThumbnailButtons() {
        const thumbnails = this.elements?.thumbnails;
        if (this.dataset.mobileProductSizing !== 'product' || this.mql?.matches !== false || !thumbnails?.slider) return false;
        const slider = thumbnails.slider;
        const end = Math.max(0, slider.scrollWidth - slider.clientWidth);
        const position = Math.max(0, Math.min(end, slider.scrollLeft));
        thumbnails.prevButton?.toggleAttribute('disabled', position <= 1);
        thumbnails.nextButton?.toggleAttribute('disabled', position >= end - 1);
        slider.style.setProperty('--thumbnail-scrolled-start', `${position}px`);
        slider.style.setProperty('--thumbnail-scrolled-end', `${end - position}px`);
        return true;
      }

      setActiveThumbnail(thumbnail, behavior = 'instant', reveal = true) {
        if (!this.elements.thumbnails || !thumbnail) return;
        if (!this.mql.matches && behavior !== 'smooth' && thumbnail.querySelector('button').getAttribute('aria-current') === 'true') return;
        if (!this.mql.matches) this.stopThumbnailScroll();

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!reveal) return;
        if (!this.mql.matches) {
          const slider = this.elements.thumbnails.slider;
          const style = getComputedStyle(slider);
          const inset = parseFloat(style.paddingLeft) || 0;
          const endInset = parseFloat(style.paddingRight) || 0;
          const start = thumbnail.offsetLeft - inset;
          const end = thumbnail.offsetLeft + thumbnail.clientWidth - slider.clientWidth + endInset;
          const position = Math.max(end, Math.min(start, slider.scrollLeft));
          if (Math.abs(position - slider.scrollLeft) <= 1) return;
          const left = Math.max(0, Math.min(slider.scrollWidth - slider.clientWidth, position));
          this.scrollThumbnailTo(left, behavior);
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
          behavior: 'auto',
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
